import type { LearnerClient, OpenerView, PlanView } from "@tutor/live";
import type { SessionGoal } from "@tutor/protocol";
import { useId, useState } from "react";
import { useLocale, useT } from "../i18n";
import { skillLabel } from "../insights/skill-labels";

// « On reprend » (feature 3): the session's first screen. Last time comes from the
// evidence, the paths are the plan's own goals (the engine's), and the child only picks
// which one leads. Nothing here is generated.

/** The paths on offer: the plan's first climb and its first review, in plan order. */
export function openerPaths(plan: PlanView): SessionGoal[] {
  const todo = plan.goals.filter((goal) => goal.state === "todo");
  const climb = todo.find((goal) => goal.kind === "climb");
  const review = todo.find((goal) => goal.kind === "review");
  return todo.filter((goal) => goal === climb || goal === review);
}

export function Opener(props: {
  opener: OpenerView;
  plan: PlanView;
  client: LearnerClient;
  /** On the Coach board, Coach has just asked the question aloud (ADR-0065 point 11). */
  board?: boolean;
}) {
  const { opener, plan, client, board = false } = props;
  const t = useT();
  const locale = useLocale();
  const headingId = useId();
  const paths = openerPaths(plan);
  const [picked, setPicked] = useState(paths[0]?.skillId ?? null);
  const [sent, setSent] = useState(false);
  const last = opener.lastTime;
  return (
    <section className="item-card opener" aria-labelledby={headingId}>
      <p className="opener-kicker">{t("opener.kicker")}</p>
      {last !== null && last.items > 0 && (
        <div className="opener-last">
          <p className="opener-last-title">{t("opener.lastTime")}</p>
          <p>
            {t("opener.lastSkill", { skill: skillLabel(last.skillId, locale) })}{" "}
            <strong>{t("opener.firstTry", { n: last.firstTry, total: last.items })}</strong>
          </p>
        </div>
      )}
      <h2 id={headingId} className={board ? "visually-hidden" : "opener-question"}>
        {t(paths.length > 1 ? "opener.question" : "opener.today")}
      </h2>
      {paths.length > 1 ? (
        <div className="opener-paths" role="radiogroup" aria-labelledby={headingId}>
          {paths.map((goal) => (
            <button
              key={goal.skillId}
              type="button"
              role="radio"
              className="opener-path"
              aria-checked={picked === goal.skillId}
              onClick={() => {
                setPicked(goal.skillId);
              }}
            >
              <PathText goal={goal} />
            </button>
          ))}
        </div>
      ) : (
        // One path: nothing to pick, so it reads as the plan, not as a lone choice.
        paths.map((goal) => (
          <p key={goal.skillId} className="opener-path opener-path-only">
            <PathText goal={goal} />
          </p>
        ))
      )}
      <button
        type="button"
        className="button primary"
        disabled={sent || picked === null}
        onClick={() => {
          if (picked === null) return;
          setSent(true);
          client.pickSession(opener.sessionStart, picked, locale);
        }}
      >
        {t("opener.go")}
      </button>
      {opener.typed === true && <p className="opener-type-hint">{t("opener.typeHint")}</p>}
    </section>
  );
}

function PathText(props: { goal: SessionGoal }) {
  const { goal } = props;
  const t = useT();
  const locale = useLocale();
  return (
    <>
      <span className="opener-path-kind">
        {t(goal.kind === "climb" ? "opener.climb" : "opener.review")}
      </span>
      <span className="opener-path-skill">{skillLabel(goal.skillId, locale)}</span>
      <span className="opener-path-why">
        {t(goal.kind === "climb" ? "opener.climbWhy" : "opener.reviewWhy")}
      </span>
    </>
  );
}
